<template>
	<Pupup @close="handleClose" :showClose="true" :width="800" title="Dates of Interest" v-if="showModal">
		<template v-slot:content>
			<div class="dates-of-interest-content">
				<div class="dates-guidance" v-if="!loading">
					<p>
						Find brands that are interested in the same occasions and/or planning similar campaigns to you.<br />
						<strong>Select the occasions your brand is planning to take part in</strong>. You can update your preferences at any time.
					</p>
				</div>

				<div class="dates-toolbar" v-if="!loading">
					<span>{{ selectedDatesLabel }}</span>
				</div>

				<div class="dates-loading" v-if="loading" aria-live="polite">
					<p class="modal-description">Loading dates of interest...</p>
					<div class="dates-grid">
						<div class="date-option skeleton" v-for="index in 6" :key="`date-skeleton-${index}`">
							<span class="date-check"></span>
							<span class="date-info">
								<span class="skeleton-line title"></span>
								<span class="skeleton-line meta"></span>
							</span>
						</div>
					</div>
				</div>

				<div class="dates-grid" v-if="!loading">
					<label
						v-for="date in datesOfInterest"
						:key="date.id"
						class="date-option"
						:class="{ selected: isSelected(date.id) }">
						<input class="date-input" type="checkbox" :id="date.id" :checked="isSelected(date.id)" @change="toggleDate(date.id)" />
						<span class="date-check" aria-hidden="true"></span>
						<span class="date-info">
							<h4>{{ date.label }}</h4>
							<span class="date-period" v-if="date.period">{{ date.period }}</span>
						</span>
					</label>
				</div>
				<p class="missing" v-if="!loading">
					Are we missing any significant dates of interest?
					<span role="button" tabindex="0" @click="openDatesFeedback" @keydown.enter.prevent="openDatesFeedback" @keydown.space.prevent="openDatesFeedback"
						>Let us know here</span
					>.
				</p>
			</div>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, watch } from "vue";
import Pupup from "./../Display/Pupup.vue";
import { useVueInstance } from "./../../composables/useVueInstance";
import ApiService from "./../../services/api.service";
export default {
	name: "DatesOfInterestModal",
	components: {
		Pupup,
	},
	props: {
		initialSelection: {
			type: Array,
			default: () => [],
		},
	},
	emits: ["close", "show"],
	setup() {
		const { route, store } = useVueInstance();

		const selectedDates = ref([]);

		const loading = ref(true);

		const showModal = ref(false);

		const recordModalClose = ref(false);

		const datesOfInterest = ref([]);

		const datesUpdated = ref(false);

		const pendingRequest = ref(null);

		const hasPendingSave = ref(false);

		const showDatesOfInterestModal = computed(() => store.state.general.showDatesOfInterestModal);
		const canShowDatesOfInterestModal = computed(() => {
			return !!store.state.auth.accessToken && !route.matched.some((record) => record.meta.public);
		});

		const selectedDatesLabel = computed(() => {
			const count = selectedDates.value.length;
			return `${count} date${count === 1 ? "" : "s"} selected`;
		});

		const updateVar = (payload) => store.commit("general/updateVar", payload);

		const closeModal = () => {
			showModal.value = false;
			recordModalClose.value = false;
		};

		const loadDatesOfInterest = () => {
			if (!canShowDatesOfInterestModal.value) {
				closeModal();
				loading.value = false;
				return Promise.resolve(false);
			}

			loading.value = true;
			return ApiService.get("brands/dates-of-interest")
				.then((response) => {
					datesOfInterest.value = response.data.dates;
					recordModalClose.value = !!response.data.show;
					selectedDates.value = datesOfInterest.value.filter((date) => date.interested).map((date) => date.id);
					return true;
				})
				.catch(() => false)
				.finally(() => {
					loading.value = false;
				});
		};

		watch(showDatesOfInterestModal, () => {
			if (showDatesOfInterestModal.value) {
				updateVar({ varName: "showDatesOfInterestModal", info: false });
				if (!canShowDatesOfInterestModal.value) {
					closeModal();
					return;
				}
				showModal.value = true;
				loadDatesOfInterest().then((loaded) => {
					if (!loaded || !canShowDatesOfInterestModal.value) closeModal();
				});
			}
		});

		watch(canShowDatesOfInterestModal, (canShow) => {
			if (!canShow) {
				closeModal();
				updateVar({ varName: "showDatesOfInterestModal", info: false });
			}
		});

		const show = (currentSelection = []) => {
			selectedDates.value = [...currentSelection];
		};

		const isSelected = (dateId) => {
			return selectedDates.value.includes(dateId);
		};

		const toggleDate = (dateId) => {
			if (isSelected(dateId)) {
				selectedDates.value = selectedDates.value.filter((id) => id !== dateId);
			} else {
				selectedDates.value.push(dateId);
			}
			handleSave();
		};

		const handleSave = () => {
			// If there's already a request in progress, mark that we need to save again
			if (pendingRequest.value) {
				hasPendingSave.value = true;
				return;
			}

			// Start the save request
			pendingRequest.value = ApiService.post("brands/dates-of-interest", { dates: selectedDates.value })
				.then(() => {
					datesUpdated.value = true;
				})
				.finally(() => {
					pendingRequest.value = null;

					// If another save was requested while we were saving, do it now
					if (hasPendingSave.value) {
						hasPendingSave.value = false;
						handleSave();
					}
				});
		};

		loadDatesOfInterest().then((loaded) => {
			showModal.value = loaded && canShowDatesOfInterestModal.value && recordModalClose.value;
		});

		const waitForPendingSavesToDrain = async () => {
			while (pendingRequest.value) {
				try {
					await pendingRequest.value;
				} catch (error) {
					break;
				}
			}
			if (hasPendingSave.value) {
				await Promise.resolve();
				return waitForPendingSavesToDrain();
			}
		};

		const handleClose = async () => {
			showModal.value = false;
			if (recordModalClose.value) {
				ApiService.post("brands/dates-of-interest/closed");
			}
			await waitForPendingSavesToDrain();
			if (datesUpdated.value) {
				//toastSuccess(`Once enough brands have added theirs, we'll notify you of your matches!`, "Dates of Interest");
				updateVar({ varName: "datesOfInterestRefreshNonce", info: Date.now() });
				datesUpdated.value = false;
			}
		};

		const openDatesFeedback = () => {
			store.dispatch("feedback/openWidget", {
				type: "feedback",
				message: "I'd love to see the following dates of interest on PartnerUp... ",
			});
		};

		return {
			selectedDates,
			datesOfInterest,
			selectedDatesLabel,
			loading,
			show,
			isSelected,
			toggleDate,
			handleSave,
			handleClose,
			openDatesFeedback,
			showModal,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";

.dates-of-interest-content {
	padding: 0;
}

.dates-guidance {
	margin-bottom: 18px;
	padding: 14px 16px;
	background: @guidedRoseBgSubtle;
	border: 1px solid @borderRoseSubtle;
	border-radius: @radiusLg;

	p {
		margin: 0;
		color: @textBody;
		font-size: 15px;
		line-height: 1.5;
	}

	strong {
		color: @textStrong;
		font-weight: 600;
	}
}

.dates-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;
	margin-bottom: 12px;
	color: @textSubtle;
	font-size: 13px;
	font-weight: 500;
}

.modal-description {
	margin: 0 0 14px;
	color: @textSubtle;
	font-size: 14px;
	line-height: 1.45;
}

.dates-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 12px;
	margin-bottom: 12px;
}

.date-option {
	position: relative;
	display: grid;
	grid-template-columns: 24px minmax(0, 1fr);
	align-items: center;
	gap: 12px;
	min-height: 74px;
	margin: 0;
	padding: 14px 16px;
	background: @surfacePanel;
	border: 1px solid @borderRoseSubtle;
	border-radius: @radiusLg;
	cursor: pointer;
	transition:
		background-color 0.18s ease,
		border-color 0.18s ease,
		box-shadow 0.18s ease,
		transform 0.18s ease;

	&:hover {
		background: @surfaceInputActive;
		border-color: @borderRose;
		transform: translateY(-1px);
		.elevation.card();
	}

	&.selected {
		background: @guidedRoseBg;
		border-color: @borderRoseStrong;

		.date-check {
			background: @guidedRose;
			border-color: @guidedRose;

			&::after {
				opacity: 1;
			}
		}

		h4 {
			color: @textStrong;
		}
	}
}

.date-input {
	position: absolute;
	width: 1px;
	height: 1px;
	opacity: 0;
	pointer-events: none;

	&:focus-visible + .date-check {
		outline: 2px solid @formDefaultColour;
		outline-offset: 3px;
	}
}

.date-check {
	position: relative;
	display: block;
	width: 20px;
	height: 20px;
	background: @surfaceInputActive;
	border: 1px solid @borderRose;
	border-radius: @radiusXs;
	transition:
		background-color 0.18s ease,
		border-color 0.18s ease;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: 6px;
		width: 5px;
		height: 10px;
		border: solid @surfacePanel;
		border-width: 0 2px 2px 0;
		opacity: 0;
		transform: rotate(45deg);
		transition: opacity 0.18s ease;
	}
}

.date-info {
	min-width: 0;

	h4 {
		margin: 0 0 3px;
		color: @textStrong;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.25;
	}

	.date-period {
		display: block;
		color: @textSubtle;
		font-size: 13px;
		line-height: 1.35;
	}
}

.missing {
	margin: 22px 0 0;
	padding: 0 16px;
	color: @guidedRose;
	font-size: 13px;
	line-height: 1;
	background: none;
	border: none;
	span {
		text-decoration: underline;
		cursor: pointer;
		color: inherit;
	}
}

.skeleton {
	cursor: default;
	pointer-events: none;

	.date-check,
	.skeleton-line {
		background: @surfaceInputActive;
		border-color: @surfaceInputActive;
	}

	.skeleton-line {
		display: block;
		height: 12px;
		border-radius: @radiusPill;
	}

	.skeleton-line.title {
		width: 62%;
		margin-bottom: 9px;
	}

	.skeleton-line.meta {
		width: 38%;
		height: 10px;
	}
}

// Responsive adjustments
@media (min-width: 768px) {
	.dates-grid {
		grid-template-columns: 1fr 1fr;
		gap: 14px;
	}
}

@media (max-width: 540px) {
	.dates-toolbar {
		align-items: flex-start;
		flex-direction: column;
		gap: 4px;
	}
}
</style>
